import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by
 * `<theme-drawer>`.
 *
 * This component handles:
 * - Auto-opening the drawer after add-to-cart.
 * - Keeping the drawer open when cart items are updated or removed.
 * - Updating sticky summary layout.
 * - Closing the drawer when the installments CTA is clicked.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /**
   * Stores whether the drawer was open before a cart update.
   * This is important because removing a product can trigger a Section
   * Rendering API update that replaces the drawer DOM.
   */
  #wasOpenBeforeCartUpdate = false;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    // The restore path sets [open] before this module loads.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  /**
   * Handles the drawer opening.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid
    // overlapping dialogs.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta =
        this.querySelector('shopify-payment-terms')
          ?.shadowRoot
          ?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * Handles cart line updates.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const drawer = this.#themeDrawer;

    /**
     * Remember whether the drawer was already open BEFORE the cart update.
     *
     * This allows us to restore it after Shopify replaces the cart drawer
     * through the Section Rendering API.
     */
    this.#wasOpenBeforeCartUpdate = Boolean(drawer?.isOpen);

    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !drawer?.isOpen;

    // When the event originates inside an open MODAL dialog
    // (e.g. quick-add), defer the auto-open until that dialog closes.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null
    );

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      drawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => {
          requestAnimationFrame(() => {
            this.#updateStickyState();
          });
        };

        if (detail?.didError) {
          settle();
          return;
        }

        /**
         * IMPORTANT:
         *
         * If the cart drawer was open before removing/updating an item,
         * keep it open after the Section Rendering API finishes replacing
         * the cart content.
         */
        const restoreDrawerState = () => {
          const currentDrawer =
            document.querySelector('theme-drawer#cart-drawer');

          if (
            this.#wasOpenBeforeCartUpdate &&
            currentDrawer &&
            !currentDrawer.isOpen
          ) {
            currentDrawer.open();
          }
        };

        const openAndSettle = () => {
          if (shouldAutoOpen && !drawer?.isOpen) {
            drawer?.open();
          }

          requestAnimationFrame(() => {
            restoreDrawerState();
            settle();
          });
        };

        if (sourceModal?.open && shouldAutoOpen) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Returns whether the cart is empty.
   */
  #isCartEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer--empty')
    );
  }

  /**
   * Updates whether the cart summary should be sticky.
   */
  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    // Refs do not cross nested component boundaries,
    // so query directly within the dialog.
    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}